import { Box, Button, HStack, IconButton, Separator, Text, VStack } from "@chakra-ui/react-v3";
import { useEffect, useRef, useState } from "react";
import { pdfjs } from "react-pdf";
import { RiCloseLine, RiDownloadLine, RiCheckLine, RiAddLine } from "react-icons/ri";
import { usePdfViewer } from "../../../shared-logic/usePdfViewer";
import { useSearchFilter } from "../../../../../hooks/useSearchFilter";
import { useSearchNavigation } from "../../../../../hooks/useSearchNavigation";
import { PdfControls } from "./pdf-controls";
import { PdfDocumentViewer } from "./pdf-document-viewer";
import { pdfjsBaseCss } from "./pdfjs-base-css";
import { Z_INDEX } from "../../constants/z-index";
import { SimpleModal } from "../simple-modal";

/** Configure pdfjs worker */
/** See https://github.com/wojtekmaj/react-pdf/discussions/1520 for source on why we do this */
// Use HTTPS explicitly to work with CSP in both local and cloud environments
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

type PdfViewerModalProps = {
  isOpen: boolean;
  onClose: () => void;
  docId: string;
  docFilename: string;
  initialSearchQuery?: string;
  onAddToExport?: (docId: string, docFilename: string) => void;
  onRemoveFromExport?: (docId: string) => void;
  isDocumentInExportList?: (docId: string) => boolean;
};

export function PdfViewerModal({
  isOpen,
  onClose,
  docId,
  docFilename,
  initialSearchQuery,
  onAddToExport,
  onRemoveFromExport,
  isDocumentInExportList,
}: PdfViewerModalProps) {
  const { isLoading, pdfUrl, error, fetchPdfUrl, clearPdfUrl } = usePdfViewer();
  /** HACK: Intentionally not passing initialSearchQuery to avoid preloading the search bar */
  const [isPdfRenderSuccessful, setIsPdfRenderSuccessful] = useState(false);

  const isPdfReady = !!pdfUrl && !isLoading && isPdfRenderSuccessful;

  const {
    filterQuery: searchText,
    filterInputRef,
    handleFilterChange,
    handleClear,
  } = useSearchFilter({ initialFilterQuery: undefined });

  const [numPages, setNumPages] = useState<number | null>(null);
  const [scale, setScale] = useState(1.75);
  const [isPanning, setIsPanning] = useState(false);
  const pdfContainerRef = useRef<HTMLDivElement>(null);

  const canMarkForExport = !!onAddToExport && !!isDocumentInExportList && !!onRemoveFromExport;

  const { currentMatchIndex, totalMatches, navigateToNext, navigateToPrevious } =
    useSearchNavigation(searchText, pdfContainerRef, {
      highlightClassName: "search-highlight",
      currentClassName: "search-highlight-current",
      enabled: isOpen && !!pdfUrl && !!searchText.trim(),
      searchInputRef: filterInputRef,
    });

  useEffect(() => {
    if (isOpen && docId) {
      fetchPdfUrl(docId, docFilename);
      setNumPages(null);
      setScale(1.75);
      setIsPdfRenderSuccessful(false);
    }
  }, [isOpen, docId, docFilename, initialSearchQuery]);

  useEffect(() => {
    if (!isOpen) {
      clearPdfUrl();
      setNumPages(null);
      setScale(1.75);
      handleClear();
      setIsPdfRenderSuccessful(false);
    }
  }, [isOpen]);

  function onDocumentLoadSuccess({ numPages }: { numPages: number }) {
    setNumPages(numPages);
    setIsPdfRenderSuccessful(true);
    filterInputRef.current?.focus();
    /** HACK: Commented out to skip preloading search bar with initialSearchQuery */
    // if (filterInputRef.current && initialSearchQuery) {
    //   handleFilterChange({
    //     target: { value: initialSearchQuery },
    //   } as React.ChangeEvent<HTMLInputElement>);
    //   filterInputRef.current.value = initialSearchQuery;
    //   filterInputRef.current.focus();
    // }
  }

  function onDocumentLoadError(error: Error) {
    console.error("Error loading PDF:", error);
  }

  function handleDownload() {
    if (pdfUrl) {
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = docFilename;
      a.target = "_blank";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
  }

  function handleToggleExport() {
    if (!canMarkForExport) return;
    if (isDocumentInExportList(docId)) {
      onRemoveFromExport(docId);
    } else {
      onAddToExport(docId, docFilename);
    }
  }

  function handleZoomIn() {
    setScale(prev => Math.min(prev + 0.25, 3.0));
  }

  function handleZoomOut() {
    setScale(prev => Math.max(prev - 0.25, 0.5));
  }

  function handleResetZoom() {
    setScale(1.0);
  }

  /** Highlight search text in the PDF text layer */
  function highlightSearchText(query: string) {
    if (!pdfContainerRef.current) return;

    /** Clear existing highlights if no query */
    if (!query.trim()) {
      const existingHighlights = pdfContainerRef.current.querySelectorAll(".search-highlight");
      existingHighlights?.forEach(el => {
        const parent = el.parentNode;
        if (parent) {
          parent.replaceChild(document.createTextNode(el.textContent ?? ""), el);
          parent.normalize();
        }
      });
      return;
    }

    /** Get all text layers from all pages */
    const textLayers = pdfContainerRef.current.querySelectorAll(".react-pdf__Page__textContent");
    if (textLayers.length === 0) return;

    /** Remove existing highlights first from all pages */
    const existingHighlights = pdfContainerRef.current.querySelectorAll(".search-highlight");
    existingHighlights.forEach(el => {
      const parent = el.parentNode;
      if (parent) {
        parent.replaceChild(document.createTextNode(el.textContent ?? ""), el);
        parent.normalize();
      }
    });

    const searchLower = query.toLowerCase();

    /** Process each text layer (page) */
    textLayers.forEach(textLayer => {
      const walker = document.createTreeWalker(textLayer, NodeFilter.SHOW_TEXT, null);
      const nodesToHighlight: { node: Text; matches: { index: number; length: number }[] }[] = [];

      let node: Node | null;
      while ((node = walker.nextNode())) {
        const textNode = node as Text;
        const text = textNode.textContent ?? "";
        const textLower = text.toLowerCase();
        const matches: { index: number; length: number }[] = [];

        let index = textLower.indexOf(searchLower);
        while (index !== -1) {
          matches.push({ index, length: query.length });
          index = textLower.indexOf(searchLower, index + 1);
        }

        if (matches.length > 0) {
          nodesToHighlight.push({ node: textNode, matches });
        }
      }

      /** Apply highlights for this text layer */
      nodesToHighlight.forEach(({ node, matches }) => {
        const text = node.textContent ?? "";
        const parent = node.parentNode;
        if (!parent) return;

        const fragment = document.createDocumentFragment();
        let lastIndex = 0;

        matches.forEach(match => {
          /** Add text before match */
          if (match.index > lastIndex) {
            fragment.appendChild(document.createTextNode(text.substring(lastIndex, match.index)));
          }

          /** Add highlighted text */
          const mark = document.createElement("mark");
          mark.className = "search-highlight";
          mark.textContent = text.substring(match.index, match.index + match.length);
          fragment.appendChild(mark);

          lastIndex = match.index + match.length;
        });

        /** Add remaining text */
        if (lastIndex < text.length) {
          fragment.appendChild(document.createTextNode(text.substring(lastIndex)));
        }

        parent.replaceChild(fragment, node);
      });
    });
  }

  /** Trigger highlighting when searchText changes */
  useEffect(() => {
    highlightSearchText(searchText);
  }, [searchText, pdfUrl]);

  function truncateFilename(filename: string): string {
    const maxLength = 60;
    if (filename.length <= maxLength) return filename;
    return `${filename.substring(0, 30)}...${filename.substring(filename.length - 27)}`;
  }

  return (
    <>
      <style>
        {`
          ${pdfjsBaseCss}

          /* Search highlight styling - overrides transparent text */
          mark.search-highlight {
            background-color: #ffeb3b !important;
            color: #000 !important;
            padding: 2px 0 !important;
            opacity: 1 !important;
          }
          
          mark.search-highlight-current {
            background-color: #ff9800 !important;
            color: #000 !important;
            padding: 2px 0 !important;
            opacity: 1 !important;
          }
        `}
      </style>
      <SimpleModal
        isOpen={isOpen}
        onClose={onClose}
        onFocusOutside={(e: FocusEvent) => {
          /** Prevent closing on focus outside (e.g., tab switching) */
          /** Only allow closing via explicit user actions like clicking backdrop or escape key */
          e.preventDefault();
        }}
        backdropZIndex={Z_INDEX.PDF_MODAL_BACKDROP}
        contentZIndex={Z_INDEX.PDF_MODAL_CONTENT}
      >
        <SimpleModal.Header
          style={{
            backgroundColor: "var(--chakra-colors-bg-subtle)",
            borderColor: "var(--chakra-colors-border-subtle)",
          }}
        >
          <HStack justify="space-between" w="full" gap={4}>
            <Box flex="1">
              <Text fontWeight="bold" fontSize="lg" color="fg">
                PDF Viewer
              </Text>
              <Text fontSize="sm" fontWeight="normal" color="fg.muted">
                {truncateFilename(docFilename)}
              </Text>
            </Box>
            <HStack gap={2}>
              {canMarkForExport && (
                <Button
                  size="sm"
                  bg={isDocumentInExportList(docId) ? "btn.success.bg" : "transparent"}
                  color={isDocumentInExportList(docId) ? "btn.success.color" : "fg"}
                  _hover={{
                    borderColor: isDocumentInExportList(docId)
                      ? "btn.success.hoverBorder"
                      : "border",
                  }}
                  variant={isDocumentInExportList(docId) ? "solid" : "outline"}
                  onClick={handleToggleExport}
                  disabled={!isPdfReady}
                >
                  {isDocumentInExportList(docId) ? (
                    <>
                      <RiCheckLine />
                      Marked for Export
                    </>
                  ) : (
                    <>
                      <RiAddLine />
                      Mark for Export
                    </>
                  )}
                </Button>
              )}
              <Button
                size="sm"
                colorScheme="blue"
                variant="outline"
                onClick={handleDownload}
                disabled={!isPdfReady}
              >
                <RiDownloadLine />
                Download
              </Button>
              <IconButton aria-label="Close" variant="ghost" size="sm" onClick={onClose}>
                <RiCloseLine />
              </IconButton>
            </HStack>
          </HStack>
        </SimpleModal.Header>
        <Separator />
        <SimpleModal.Body style={{ padding: 0 }}>
          <VStack align="stretch" gap={0} h="70vh">
            {/* PDF Controls Toolbar */}
            <PdfControls
              searchText={searchText}
              filterInputRef={filterInputRef}
              handleFilterChange={handleFilterChange}
              handleClear={handleClear}
              totalMatches={totalMatches}
              currentMatchIndex={currentMatchIndex}
              navigateToNext={navigateToNext}
              navigateToPrevious={navigateToPrevious}
              scale={scale}
              handleZoomIn={handleZoomIn}
              handleZoomOut={handleZoomOut}
              handleResetZoom={handleResetZoom}
              isPanning={isPanning}
              isLoading={!isPdfReady}
              setIsPanning={setIsPanning}
              numPages={numPages}
            />

            {/* PDF Document Content */}
            <PdfDocumentViewer
              pdfUrl={pdfUrl}
              error={error}
              isLoading={isLoading}
              pdfContainerRef={pdfContainerRef}
              isPanning={isPanning}
              numPages={numPages}
              scale={scale}
              onDocumentLoadSuccess={onDocumentLoadSuccess}
              onDocumentLoadError={onDocumentLoadError}
            />
          </VStack>
        </SimpleModal.Body>
      </SimpleModal>
    </>
  );
}
